import React, { useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteIcon from '@mui/icons-material/Delete';

import { PageHeader, DataTableCard, FilterChips, ConfirmDialog, FeatureUsageMeter } from '../ui';
import AdvertisementWizard from './AdvertisementWizard';
import buildAdvertisementColumns, { AdvertisementRowMenu } from './buildAdvertisementColumns';
import { ADVERTISEMENT_STATUSES } from '../../constants/advertisementTemplates';
import useAdvertisementsPage from './useAdvertisementsPage';

const Advertisements = () => {
  const p = useAdvertisementsPage();
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuAd, setRowMenuAd] = useState(null);

  const openRowMenu = (event, ad) => {
    setRowMenuAnchor(event.currentTarget);
    setRowMenuAd(ad);
  };
  const closeRowMenu = () => setRowMenuAnchor(null);

  const columns = buildAdvertisementColumns({ t: p.t, openRowMenu, handleToggleActive: p.handleToggleActive });

  const filterOptions = [
    { value: 'all', label: p.t('All') },
    ...Object.entries(ADVERTISEMENT_STATUSES).map(([value, info]) => ({ value, label: p.t(info.label) })),
  ];

  return (
    <Box>
      <PageHeader
        title={p.t('Advertisements')}
        subtitle={p.t('Banners shown to your customers in the shop', { defaultValue: 'Banners shown to your customers in the shop' })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search advertisements...') }}
        actions={[
          {
            label: p.t('Create Advertisement'), icon: AddIcon, variant: 'contained', onClick: p.openCreateWizard,
            disabled: p.planLimit?.atLimit, disabledReason: p.planLimit?.tooltip,
          },
        ]}
        helpKey="advertisements"
        meter={<FeatureUsageMeter feature="advertisements.active.max" label={p.t('Active campaigns', { defaultValue: 'Active campaigns' })} />}
      />

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        toolbar={(
          <FilterChips options={filterOptions} value={p.statusFilter} onChange={p.setStatusFilter} filtersLabel={p.t('Filters')} />
        )}
        empty={p.isNoMatch ? {
          kind: 'no-match',
          title: p.t('No advertisements match "{{query}}"', { query: p.search, defaultValue: `No advertisements match "${p.search}"` }),
          description: p.t('Try a different search or clear the filter.', { defaultValue: 'Try a different search or clear the filter.' }),
          actions: [{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); p.setStatusFilter('all'); } }],
        } : {
          kind: 'none',
          icon: CampaignIcon,
          title: p.t('No advertisements yet'),
          description: p.t('Create your first advertisement'),
          actions: [{ label: p.t('Create Advertisement'), variant: 'contained', onClick: p.openCreateWizard }],
        }}
        error={{
          title: p.t("Couldn't load advertisements", { defaultValue: "Couldn't load advertisements" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: () => p.fetchAdvertisements(true),
        }}
        permission={{
          title: p.t("You don't have access to Advertisements", { defaultValue: "You don't have access to Advertisements" }),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        onRowClick={(params, event) => {
          if (event.target.closest('button')) return;
          p.openEditWizard(params.row);
        }}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <AdvertisementRowMenu
        t={p.t}
        anchorEl={rowMenuAnchor}
        ad={rowMenuAd}
        onClose={closeRowMenu}
        onDuplicate={p.handleDuplicate}
        onDelete={p.openDeleteConfirm}
      />

      <AdvertisementWizard
        open={p.wizardOpen}
        onClose={p.closeWizard}
        onSuccess={p.handleWizardSuccess}
        editingAdvertisement={p.editingAd}
        companyId={p.companyId}
      />

      <ConfirmDialog
        open={p.confirmOpen}
        onClose={() => p.setConfirmOpen(false)}
        tone="danger"
        icon={DeleteIcon}
        title={p.t('Delete "{{title}}"?', { title: p.confirmTarget?.title, defaultValue: `Delete "${p.confirmTarget?.title}"?` })}
        description={p.t('This permanently deletes the advertisement. This action cannot be undone.', { defaultValue: 'This permanently deletes the advertisement. This action cannot be undone.' })}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Delete', { defaultValue: 'Delete' })}
        onConfirm={p.handleConfirmDelete}
        loading={p.deleting}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Advertisements;
